/**
 * The frame every public scan screen sits in: the bar with the mark across the
 * top, then the body on the second paper.
 *
 * It exists so the overlay the homepage shows while a scan is starting and the
 * scan page that replaces it are literally the same markup. The handoff
 * between them happens on one commit, and if both draw the same frame there
 * is nothing for the eye to catch: the scan page's first frame is the
 * overlay's last one. Two hand-built copies of this bar had already drifted
 * once (one carried padding the other did not), which is the seam this file
 * closes.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { DsIsland } from '@/design-system/island'
import { Check } from 'lucide-react'
import { BrandMark } from '@/design-system/primitives'

/**
 * The path, named once. Sign-up first: the account, then the site, then
 * the report. Three things in the order they happen, and none of them is
 * paying or connecting: connecting HubSpot is a step on the dashboard, and
 * the first page is free. Same wizard the agency and managed forms wear,
 * so the sequence looks like something this product does everywhere.
 */
export const ONBOARDING_STEPS = ['Sign up', 'Your site', 'Your report'] as const

/**
 * The path as it was when the scan came first. Kept for the screens that
 * still walk it: anonymous scans already in flight, and every scan while
 * the switch is off.
 */
export const SCAN_FIRST_STEPS = ['Read your site', 'Sign up', 'Connect HubSpot', 'Put it live'] as const

export interface OnboardingStepsState {
  /** Index of the step the person is on, in the row named by `path`. */
  current: number
  /** True once the last step is finished: every step reads as done. */
  done?: boolean
  /** Steps the person chose to pass, by index. Neither done nor next: nothing happened there. */
  skipped?: number[]
  /**
   * Which row to draw. Left out, it is the scan-first row, which is what
   * every screen written before sign-up first passes.
   */
  path?: 'signup_first' | 'scan_first'
}
import '@/design-system/onboarding.css'

export function PublicScanFrame({
  children,
  className,
  bodyClassName,
  steps,
}: {
  children: ReactNode
  className?: string
  /** The wizard row under the bar. Omitted on screens that are not on the path. */
  steps?: OnboardingStepsState
  /** A width modifier for the body, for the one screen that needs more than 980px. */
  bodyClassName?: string
}) {
  /*
    Whether the person already has an account. The sequence makes accounts
    (the sign-up step), and for a while it had no door out of one: somebody
    who signed up here and wanted to be somebody else, a second account, a
    colleague at the same desk, could only leave by clearing the browser.
  */
  const { isLoaded, isSignedIn, signOut } = useAuth()
  const labels: readonly string[] = steps?.path === 'signup_first' ? ONBOARDING_STEPS : SCAN_FIRST_STEPS
  return (
    <DsIsland panel={false} ground={false} className={className ? `ds-oshell ${className}` : 'ds-oshell'}>
      <header className="ds-obar">
        {/*
          The same mark the masthead and the auth shell carry. This bar kept
          the old purple bolt for months after the rest of the sequence
          moved, so the first screen a new customer saw wore a different
          brand from the one they had just left.
        */}
        <Link to="/" className="ds-obar__brand" aria-label="SuperSchema">
          <BrandMark className="ds-obar__logo" size={26} />
          <span aria-hidden="true">SuperSchema</span>
        </Link>
        {/* No sign-in on this bar. The sequence is for somebody who does not
            have an account yet, and a door out of it at every step was one
            more thing to read on the way to the one thing to do. The one
            exception is the person who already has the account this
            sequence made: they get a quiet way out of it, in words, on the
            far side of the bar. Signed out, they land on the homepage. */}
        {isLoaded && isSignedIn ? (
          <button
            type="button"
            className="ds-obar__out"
            onClick={() => { void signOut({ redirectUrl: '/' }) }}
          >
            Sign out
          </button>
        ) : null}
      </header>
      <main className={bodyClassName ? `ds-oshell__body ${bodyClassName}` : 'ds-oshell__body'}>
        {steps ? (
          /*
            The path, drawn the way the wizard forms draw theirs, with two
            things those forms do not need. Each step carries its number in
            a disc, because a bar and a word were easy to miss on a page with
            a photograph on it. And the last step is set apart in the
            confirmed blue before anybody reaches it: it is the goal, and the
            person is being walked to it; blue is what "confirmed live" will
            be when they get there. The row keeps one narrow measure on every
            screen, so it does not widen and narrow as the pages change width.
          */
          <ol className="ds-osteps" aria-label="Progress" data-testid="onboarding-steps">
            {labels.map((label, i) => {
              const skipped = !steps.done && (steps.skipped?.includes(i) ?? false)
              const done = steps.done || (i < steps.current && !skipped)
              const current = !steps.done && i === steps.current
              const state = done ? 'done' : current ? 'current' : skipped ? 'skipped' : 'next'
              return (
                <li
                  key={label}
                  className="ds-osteps__step"
                  data-state={state}
                  data-goal={i === labels.length - 1 || undefined}
                  aria-current={current ? 'step' : undefined}
                >
                  <span className="ds-osteps__bar" aria-hidden="true" />
                  <span className="ds-osteps__row">
                    <span className="ds-osteps__disc" aria-hidden="true">{done ? <Check /> : i + 1}</span>
                    <span className="ds-osteps__label">
                      <span className="ds-osteps__n">Step {i + 1}</span>
                      {label}
                    </span>
                  </span>
                </li>
              )
            })}
          </ol>
        ) : null}
        {children}
      </main>
    </DsIsland>
  )
}
